/**
 * File Preview Component
 *
 * A versatile component that can display previews for different file types:
 * - Images: Show as thumbnails
 * - PDFs: Show with PDF icon and document info
 * - Other files: Show with file-type-specific icons
 */

import React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:file-preview');
import { FileText, FileSpreadsheet, FileJson, File, FileCode, Sheet } from 'lucide-react';
import { cn } from '../utils/cn';
import { truncateFilename } from '../utils/filename-truncate';

export interface FilePreviewProps {
  id: string;
  name: string;
  contentType: string;
  url: string; // base64 data URL
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  showFileName?: boolean;
  extractedText?: string; // For PDFs, show a preview of extracted text
}

const FiletypeIcon: React.FC<{ contentType: string; className?: string; name?: string }> = ({
  contentType,
  className = 'w-6 h-6',
  name = '',
}) => {
  const extension = name.split('.').pop()?.toLowerCase();

  // PDF - Red
  if (contentType === 'application/pdf') {
    return (
      <div className="relative">
        <FileText className={cn('text-red-500', className)} strokeWidth={2} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[8px] font-bold text-red-600 mt-1">PDF</span>
        </div>
      </div>
    );
  }

  // Excel/Spreadsheets - Green
  if (
    contentType === 'text/csv' ||
    extension === 'csv' ||
    contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    contentType === 'application/vnd.ms-excel' ||
    extension === 'xlsx' ||
    extension === 'xls'
  ) {
    return (
      <div className="relative">
        <FileSpreadsheet className={cn('text-green-600', className)} strokeWidth={2} />
        {extension === 'csv' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[7px] font-bold text-green-700 mt-2">CSV</span>
          </div>
        )}
        {(extension === 'xlsx' || extension === 'xls') && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[7px] font-bold text-green-700 mt-2">XLS</span>
          </div>
        )}
      </div>
    );
  }

  // JSON - Orange/Yellow
  if (contentType === 'application/json' || extension === 'json') {
    return (
      <div className="relative">
        <FileJson className={cn('text-amber-600', className)} strokeWidth={2} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[7px] font-bold text-amber-700 mt-2">JSON</span>
        </div>
      </div>
    );
  }

  // Word Documents - Blue
  if (
    contentType === 'application/msword' ||
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    extension === 'doc' ||
    extension === 'docx'
  ) {
    return (
      <div className="relative">
        <FileText className={cn('text-blue-600', className)} strokeWidth={2} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[7px] font-bold text-blue-700 mt-2">DOC</span>
        </div>
      </div>
    );
  }

  // Plain Text/Markdown - Blue
  if (contentType.startsWith('text/') || extension === 'txt' || extension === 'md') {
    return (
      <div className="relative">
        <FileText className={cn('text-blue-500', className)} strokeWidth={2} />
        {extension === 'md' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[8px] font-bold text-blue-600 mt-2">MD</span>
          </div>
        )}
      </div>
    );
  }

  // Code files - Purple
  if (
    extension === 'js' ||
    extension === 'ts' ||
    extension === 'tsx' ||
    extension === 'jsx' ||
    extension === 'py' ||
    extension === 'java' ||
    extension === 'cpp' ||
    extension === 'c'
  ) {
    return <FileCode className={cn('text-purple-600', className)} strokeWidth={2} />;
  }

  // Generic file icon - Gray
  return <File className={cn('text-gray-500', className)} strokeWidth={2} />;
};

const getTextPreview = (text: string, maxLength = 60): string => {
  if (text.length <= maxLength) {
    return text;
  }

  // Try to break at word boundary
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.7) {
    // If we found a space in the last 30% of the text
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
};

export const FilePreview: React.FC<FilePreviewProps> = ({
  id,
  name,
  contentType,
  url,
  size = 'md',
  className,
  onClick,
  showFileName = false,
  extractedText,
}) => {
  const isImage = contentType.startsWith('image/');
  const isPdf = contentType === 'application/pdf';

  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'w-8 h-8',
      icon: 'w-4 h-4',
      text: 'text-xs',
    },
    md: {
      container: 'w-12 h-12',
      icon: 'w-6 h-6',
      text: 'text-xs',
    },
    lg: {
      container: 'w-16 h-16',
      icon: 'w-8 h-8',
      text: 'text-base',
    },
  };

  const config = sizeConfig[size];

  const baseClasses = cn(
    config.container,
    'rounded border border-border shadow-sm',
    onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : 'cursor-default',
    className
  );

  if (isImage) {
    // For images, show the actual image thumbnail
    return (
      <div className="flex flex-col items-center gap-1">
        <img
          src={url}
          alt={name}
          className={baseClasses}
          style={{ objectFit: 'cover' }}
          title={`${name}${onClick ? ' - Click to view full size' : ''}`}
          onClick={onClick}
        />
        {showFileName && (
          <span
            className={cn(
              'text-muted-foreground text-center h-fit max-w-16 break-words',
              config.text
            )}
            title={name}
          >
            {truncateFilename(name, 20)}
          </span>
        )}
      </div>
    );
  }

  // Get background color based on file type
  const getFileTypeBackground = (contentType: string, name: string) => {
    const extension = name.split('.').pop()?.toLowerCase();

    if (isPdf) return 'bg-red-50 dark:bg-red-950/20';
    if (contentType === 'text/csv' || extension === 'csv')
      return 'bg-green-50 dark:bg-green-950/20';
    if (
      contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      contentType === 'application/vnd.ms-excel' ||
      extension === 'xlsx' ||
      extension === 'xls'
    )
      return 'bg-green-50 dark:bg-green-950/20';
    if (contentType === 'application/json' || extension === 'json')
      return 'bg-amber-50 dark:bg-amber-950/20';
    if (
      contentType === 'application/msword' ||
      contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      extension === 'doc' ||
      extension === 'docx'
    )
      return 'bg-blue-50 dark:bg-blue-950/20';
    if (contentType.startsWith('text/')) return 'bg-blue-50 dark:bg-blue-950/20';
    return 'bg-muted';
  };

  if (isPdf) {
    // For PDFs, show a nice preview with icon and text excerpt
    const textPreview = extractedText ? getTextPreview(extractedText) : null;

    return (
      <div className="flex flex-col items-center gap-1">
        <div
          className={cn(
            baseClasses,
            getFileTypeBackground(contentType, name),
            'flex flex-col items-center justify-center p-2'
          )}
          onClick={onClick}
          title={`${name}${textPreview ? ` - ${textPreview}` : ''}${onClick ? ' - Click to download' : ''}`}
        >
          <FiletypeIcon contentType={contentType} name={name} className={config.icon} />
        </div>

        {(showFileName || textPreview) && (
          <div className="flex flex-col items-center gap-0.5 max-w-16">
            {showFileName && (
              <span className={cn('text-muted-foreground text-center', config.text)} title={name}>
                {truncateFilename(name, 20)}
              </span>
            )}
            {textPreview && size !== 'sm' && (
              <span className="text-xs text-muted-foreground text-center leading-tight">
                {textPreview}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // For other file types, show file-type-specific icons
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={cn(
          baseClasses,
          getFileTypeBackground(contentType, name),
          'flex items-center justify-center p-2'
        )}
        onClick={onClick}
        title={`${name}${onClick ? ' - Click to download' : ''}`}
      >
        <FiletypeIcon contentType={contentType} name={name} className={config.icon} />
      </div>
      {showFileName && (
        <span
          className={cn('text-muted-foreground text-center max-w-16', config.text)}
          title={name}
        >
          {truncateFilename(name, 20)}
        </span>
      )}
    </div>
  );
};

// Helper hook to extract PDF metadata for previews
export const usePDFPreviewData = (files: Array<{ id: string; contentType: string }>) => {
  const [pdfTexts, setPdfTexts] = React.useState<Map<string, string>>(new Map());

  React.useEffect(() => {
    const loadPDFTexts = async () => {
      const newPdfTexts = new Map<string, string>();

      for (const file of files) {
        if (file.contentType === 'application/pdf') {
          try {
            // Try to get cached PDF text from bucket store metadata or similar
            // This is a placeholder - in practice you might want to store extracted text
            // in the bucket store metadata when PDFs are uploaded
            // For now, we'll just show the filename as preview
            newPdfTexts.set(file.id, 'PDF document');
          } catch (error) {
            logger.warn(`Failed to load PDF preview for ${file.id}:`, error);
          }
        }
      }

      setPdfTexts(newPdfTexts);
    };

    loadPDFTexts();
  }, [files]);

  return pdfTexts;
};
